/** Shared IndexedDB + OS-notification bridge with public/OneSignalSDKWorker.js */

export const MW_CALL_PUSH_TYPE = "MW_CALL_PUSH";

const MW_CALL_DB = "mw-call-push";
const MW_CALL_STORE = "pending";

const CALL_CODE_RANK: Record<string, number> = {
  setup: 1,
  proceeding: 2,
  answered: 3,
  disconnected: 4,
};

function openCallPushDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(MW_CALL_DB, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(MW_CALL_STORE)) {
        db.createObjectStore(MW_CALL_STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export type PendingCallPushMessage = {
  type: typeof MW_CALL_PUSH_TYPE;
  notification: {
    title?: string;
    body?: string;
    additionalData?: Record<string, any>;
  };
  receivedAt?: number;
};

/** Dig call additionalData out of OneSignal / FCM notification.data shapes */
export function extractCallAdditionalData(
  data: any,
): Record<string, any> | null {
  if (!data || typeof data !== "object") return null;

  if (data.eventType) return data;
  if (data.additionalData?.eventType) return data.additionalData;
  // OneSignal sometimes flattens custom.a onto data.a
  if (data.a?.eventType) return data.a;

  let custom = data.custom;
  if (typeof custom === "string") {
    try {
      custom = JSON.parse(custom);
    } catch (e) {
      custom = null;
    }
  }
  if (custom?.a?.eventType) return custom.a;
  if (custom?.eventType) return custom;

  if (data.FCM_MSG) {
    const nested = extractCallAdditionalData(
      data.FCM_MSG?.data || data.FCM_MSG,
    );
    if (nested) return nested;
  }

  if (data.data && data.data !== data) {
    const nested = extractCallAdditionalData(data.data);
    if (nested) return nested;
  }

  return null;
}

function isCallLifecycle(additionalData: Record<string, any> | null) {
  const type = String(additionalData?.eventType || "").toLowerCase();
  return type === "call_inbound" || type === "call_outbound";
}

/** Read + clear the latest background call push (if any). */
export async function takePendingCallPush(): Promise<PendingCallPushMessage | null> {
  try {
    const db = await openCallPushDb();
    const message = await new Promise<PendingCallPushMessage | null>(
      (resolve, reject) => {
        const tx = db.transaction(MW_CALL_STORE, "readwrite");
        const store = tx.objectStore(MW_CALL_STORE);
        const getReq = store.get("latest");

        let value: PendingCallPushMessage | null = null;

        getReq.onsuccess = () => {
          value = (getReq.result as PendingCallPushMessage) || null;
          if (value) store.delete("latest");
        };

        tx.oncomplete = () => resolve(value);
        tx.onerror = () => reject(tx.error);
      },
    );
    db.close();

    if (!message || message.type !== MW_CALL_PUSH_TYPE) return null;
    return message;
  } catch (e) {
    return null;
  }
}

/**
 * Read currently displayed browser notifications (still on screen).
 * OneSignal decrypts the push before showing it, so notification.data is
 * readable here even when the raw push payload is not.
 */
export async function readCallPushFromActiveNotifications(): Promise<PendingCallPushMessage | null> {
  if (!("serviceWorker" in navigator)) return null;

  try {
    const registration = await navigator.serviceWorker.ready;
    const notifications = await registration.getNotifications();

    let best: PendingCallPushMessage | null = null;
    let bestRank = -1;

    for (const notification of notifications) {
      const additionalData = extractCallAdditionalData(notification.data);
      if (!isCallLifecycle(additionalData)) continue;

      const code = String(additionalData?.code || "").toLowerCase();
      const rank = CALL_CODE_RANK[code] || 0;

      const message: PendingCallPushMessage = {
        type: MW_CALL_PUSH_TYPE,
        notification: {
          title: notification.title,
          body: notification.body,
          additionalData: additionalData || undefined,
        },
        receivedAt: Date.now(),
      };

      if (rank >= bestRank) {
        bestRank = rank;
        best = message;
      }
    }

    return best;
  } catch (e) {
    return null;
  }
}
